import Icon from '@/components/public/ui/Icon';

interface ViewToggleProps {
  options: { label: string; value: string; icon?: string }[];
  active: string;
  onChange: (value: string) => void;
  /** Fixed pixel width per button. Ignored when `fluid` is true. */
  buttonWidth?: number;
  /** Stretch to fill the container; each button gets equal share. */
  fluid?: boolean;
  /** `segmented` (default, boxed pill) or `underline` (editorial tabs). */
  variant?: 'segmented' | 'underline';
  className?: string;
}

// Light segmented control: a track on `background` with a white pill that
// slides under the active option. `variant="underline"` renders editorial tabs
// (matches the CategoryFilter) instead.
export default function ViewToggle({
  options,
  active,
  onChange,
  buttonWidth = 128,
  fluid = false,
  variant = 'segmented',
  className = '',
}: ViewToggleProps) {
  if (variant === 'underline') {
    return (
      <div role="tablist" className={`inline-flex gap-5 ${className}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active === option.value}
            onClick={() => onChange(option.value)}
            className={`inline-flex items-center gap-2 border-b-2 pb-2 text-sm transition-colors duration-200 ${
              active === option.value
                ? 'border-primary text-primary font-semibold'
                : 'text-on-surface-light hover:text-on-surface border-transparent font-medium'
            }`}
          >
            {option.icon && <Icon name={option.icon} size="sm" />}
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  const activeIndex = options.findIndex((o) => o.value === active);
  const buttonShare = 100 / options.length;

  const pillStyle = fluid
    ? {
        left: '4px',
        right: '4px',
        width: `calc(${buttonShare}% - 4px)`,
        transform: `translateX(${activeIndex * 100}%)`,
      }
    : {
        left: `${4 + activeIndex * buttonWidth}px`,
        width: `${buttonWidth}px`,
      };

  return (
    <div
      className={`border-line bg-background relative ${fluid ? 'flex w-full' : 'inline-flex'} h-11 items-center rounded-md border p-1 ${className}`}
    >
      {/* sliding pill */}
      <div
        className="bg-surface absolute top-1 bottom-1 rounded-sm shadow-sm transition-all duration-200"
        style={pillStyle}
      />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={active === option.value}
          className={`relative z-10 inline-flex h-full items-center justify-center gap-2 rounded-sm text-sm font-medium transition-colors duration-200 ${fluid ? 'min-w-0 flex-1' : ''} ${
            active === option.value
              ? 'text-primary font-semibold'
              : 'text-on-surface-light hover:text-on-surface'
          }`}
          style={fluid ? undefined : { width: `${buttonWidth}px` }}
        >
          {option.icon && <Icon name={option.icon} size="sm" />}
          <span className="truncate">{option.label}</span>
        </button>
      ))}
    </div>
  );
}
